/* /e-invoice — the page: drop anything an invoice arrives in, get the answer.
 *
 * Flow per dropped file: unpack (ZIP, .eml, PDF attachments) → classify every
 * leaf → read it (EN 16931 / NF-e / УПД / ZATCA) → verdict at once → the
 * official Schematron in the background (EN 16931 family) → the verdict again.
 * One file opens straight into its reading view; several become a list for
 * the accountant (sort, open one, all PDFs as a ZIP, CSV of totals and lines).
 *
 * Cap (config/tiers.php, decided before code): the unit is the FILE. The
 * headline answer (what it is, valid or not, who, how much) is diagnostics and
 * never bills; the full document, the explained findings and every download
 * cost one unit per file per day — a repeat of the same file is free.
 */
import { parseXml, decodeXmlBytes, XmlError, normLocation, declaredEncodingProblem } from './xml-lite.js';
import { NS } from './ns.js';
import { sniff, classifyXml, formatLabel } from './detect.js';
import { readUbl, readCii } from './en16931-read.js';
import { scenarioFor, runScenario, prefetch } from './validate.js';
import { readPdf, unpack } from './containers.js';
import * as N from './nfe.js';
import * as Z from './zatca.js';
import * as VD from './verdict.js';
import { explain } from './explain.js';
import { renderEn16931, markFields } from './render-en.js';
import { renderDanfeA4, renderDanfeNfce, renderDanfeLabel, watermarkOf, brDoc, chaveGroups } from './render-danfe.js';
import { readUpd } from './upd.js';
import { renderUpd } from './render-upd.js';
import { cmsCertificates } from './der.js';
import { h, fill, money, date, bidi } from './dom.js';
import { initCreate } from './create-ui.js';

const SLUG = 'e-invoice';
const T = window.__EI_I18N || { ui: {}, v: {}, l: {}, codes: {} };
const CFG = window.__EI_CFG || {};
const ui = (k, p) => fill((T.ui && T.ui[k]) || k, p);
const vtext = (r) => (r ? fill((T.v && T.v[r.key]) || r.key, r.params) : '');
const $ = (id) => document.getElementById(id);
const ae = (name, data) => { try { window.__ae && window.__ae(name, data || {}); } catch (_) {} };
const stage = (name, data) => { try { window.__stage && window.__stage(name, data || {}); } catch (_) {} };

/* ── lazy libraries (own copies first: CFG carries the cdn_chain lists) ── */
const libs = {};
function lib(name) {
  if (libs[name]) return libs[name];
  const spec = {
    pdfLib: [CFG.pdfLib || '/vendor/pdf-lib/pdf-lib.min.js', 'PDFLib'],
    jszip: [CFG.jszip || '/vendor/jszip/jszip.min.js', 'JSZip'],
    qrDecode: [CFG.qrDecode || '/js/qr-decode.js', '__qrDecode'],
    qrGen: [CFG.qrGen || '/vendor/qrcode/qrcode.min.js', 'qrcode'],
    batch: [CFG.batch || '/js/batch-deliver.js', '__batchDeliver'],
  }[name];
  libs[name] = window.__vendorScript(spec[0], { expect: spec[1], label: 'e-invoice-' + name })
    .then(() => window[spec[1]])
    .catch((e) => { delete libs[name]; throw e; });
  return libs[name];
}

/* ── locale data for explanations (BT names, templates, rules) ── */
let localeData = null;
function loadLocaleData() {
  if (!localeData) {
    localeData = fetch(CFG.i18nUrl, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return localeData;
}

/* ── state ─────────────────────────────────────────────────────────── */
const S = { docs: [], cur: -1, seq: 0, busy: 0, gen: 0, filter: 'all' };

function newDoc(leaf, top) {
  return {
    uid: ++S.seq,
    name: leaf.name || 'file',
    path: [...(leaf.from || []).map((x) => x.replace(/^\w+:/, '')), leaf.path || leaf.name].join(' › '),
    bytes: leaf.bytes,
    kind: leaf.kind,
    key: keyFor(top, leaf),
    status: 'reading',
    verdict: null,
  };
}

function keyFor(top, leaf) {
  const base = window.__toolLimit && window.__toolLimit.fileKey && top ? window.__toolLimit.fileKey(top) : (top ? `${top.name}|${top.size}|${top.lastModified}` : leaf.name);
  return (leaf.from && leaf.from.length) || leaf.path ? `${base}#${leaf.path || leaf.name}` : base;
}

/* ── reading ───────────────────────────────────────────────────────── */

async function readXmlDoc(d, bytes, container) {
  let text, root;
  try {
    ({ text } = decodeXmlBytes(bytes));
    ({ root } = parseXml(text));
  } catch (e) {
    d.status = 'done';
    d.cls = { format: 'broken-xml', family: 'other' };
    d.label = 'XML';
    d.verdict = { tone: 'bad', title: { key: 't_xml_broken', params: {} }, reasons: [{ key: 'xml_broken', params: { line: e.line || '?', col: e.col || '?', code: e.code || '' } }] };
    d.xmlText = text || '';
    return;
  }
  d.xmlText = text;
  d.root = root;
  d.encBad = declaredEncodingProblem(bytes) || '';
  const cls = classifyXml(root, d.name);
  d.cls = cls;
  d.label = formatLabel(cls);
  d.container = container || null;
  if (cls.family === 'en16931') {
    d.model = cls.format === 'cii' ? readCii(root) : readUbl(root);
    if (cls.zatca) return readZatcaXml(d, root);
    d.scenario = scenarioFor(cls);
    d.verdict = VD.verdictEn16931({ cls, model: d.model, scenario: d.scenario, findings: d.scenario ? null : [], container, encodingBad: d.encBad });
    d.status = d.scenario ? 'checking' : 'done';
    return;
  }
  if (cls.family === 'zugferd1') { d.verdict = VD.verdictZugferd1(); d.status = 'done'; return; }
  if (cls.family === 'nfe') {
    if (cls.format === 'nfe-event') {
      d.event = N.readNfeEvent(root);
      d.verdict = VD.verdictNfeEvent(d.event);
      d.status = 'done';
      return;
    }
    if (cls.format === 'nfe' || cls.format === 'nfe-proc' || (cls.format === 'nfe-lote' && cls.count)) {
      // A lote de envio carries the NF-e(s) the ERP sent to SEFAZ: read the first one.
      d.lote = cls.format === 'nfe-lote' ? cls.count : 0;
      d.nfe = N.readNfe(cls.format === 'nfe-lote' ? root.childrenNamed(NS.nfe, 'NFe')[0] : root);
      d.nfeCheck = N.checkNfe(d.nfe);
      d.verdict = VD.verdictNfe({ model: d.nfe, check: d.nfeCheck, sig: null, events: [], lote: d.lote });
      d.status = 'checking';
      N.verifyNfeSignature(d.nfe, crypto.subtle).then((sig) => {
        d.nfeSig = sig;
        d.status = 'done';
        refreshNfeVerdicts();
        reportVerdict(d);
      }).catch(() => { d.status = 'done'; update(d); reportVerdict(d); });
      return;
    }
    d.verdict = VD.verdictUnknown({ root: cls.root || cls.format });
    d.status = 'done';
    return;
  }
  if (cls.family === 'upd') {
    try { d.upd = cls.fragment ? null : readUpd(root); } catch (e) { d.upd = null; }
    d.verdict = VD.verdictUpd({ cls, model: d.upd, cms: null });
    if (d.encBad) d.verdict = { tone: 'bad', title: { key: 't_xml_encoding', params: { name: d.encBad } }, reasons: [{ key: 'xml_encoding_why', params: { name: d.encBad } }, ...d.verdict.reasons] };
    d.status = 'done';
    return;
  }
  if (cls.family === 'cte' || cls.family === 'nfse' || cls.format === 'mdfe' || cls.family === 'fatturapa') {
    d.verdict = { tone: 'info', title: { key: 't_known_not_viewed', params: { format: d.label } }, reasons: [{ key: 'known_not_viewed', params: {} }] };
    d.status = 'done';
    return;
  }
  d.verdict = VD.verdictUnknown(cls);
  d.status = 'done';
}

/* ZATCA phase 2: tag 7 against the key in tag 8 — secp256k1, which WebCrypto does
   not have; @noble/secp256k1 (MIT, BigInt) is imported only when such a QR appears. */
let secpP = null;
async function zatcaSignature(qr) {
  if (!qr || qr.error || !qr.tags[7] || !qr.tags[8] || typeof BigInt !== 'function' || !CFG.secp) return null;
  try {
    if (!secpP) secpP = import(CFG.secp).catch((e) => { secpP = null; throw e; });
    return await Z.verifySignature(qr, crypto.subtle, await secpP);
  } catch (_) { return null; }
}

async function readZatcaXml(d, root) {
  const qrText = Z.qrFromXml(root);
  const qr = qrText ? Z.decodeTlv(qrText) : null;
  if (qr && !qr.error) qr.problems = Z.checkPhase1(qr);
  d.zatca = { qr, qrText, xml: {} };
  d.verdict = VD.verdictZatca({ qr, xml: null });
  d.status = 'checking';
  try {
    if (qr && !qr.error && qr.tags[6]) {
      const mine = await Z.invoiceHash(root, crypto.subtle);
      d.zatca.xml = { hash: mine, hashOk: mine === Z.tagHash(qr) };
    }
    if (qr && !qr.error) qr.sigOk = await zatcaSignature(qr);
  } catch (_) {}
  d.verdict = VD.verdictZatca({ qr, xml: d.zatca.xml && d.zatca.xml.hash ? d.zatca.xml : null });
  d.status = 'done';
}

async function readPdfDoc(d) {
  const PDFLib = await lib('pdfLib');
  let info;
  try { info = await readPdf(d.bytes, PDFLib); } catch (e) {
    d.status = 'done';
    d.label = 'PDF';
    d.verdict = { tone: 'bad', title: { key: 't_pdf_unreadable', params: {} }, reasons: [{ key: 'pdf_unreadable', params: {} }] };
    return;
  }
  d.pdfBytes = d.bytes;
  const xmls = info.attachments.filter((a) => /\.xml$/i.test(a.name) || /xml/.test(a.mime));
  const prefer = ['factur-x.xml', 'zugferd-invoice.xml', 'xrechnung.xml', 'zugferd_invoice.xml'];
  xmls.sort((a, b) => (prefer.indexOf(b.name.toLowerCase()) + 1 || 0) - (prefer.indexOf(a.name.toLowerCase()) + 1 || 0));
  d.pdfAttachments = info.attachments;
  for (const a of xmls) {
    const sub = {};
    await readXmlDoc(sub, a.bytes, { kind: 'pdf', pdfa: info.pdfa, fx: info.fx, xmlName: a.name, afRel: a.afRel, attachments: info.attachments });
    if (sub.cls && (sub.cls.family === 'en16931' || sub.cls.family === 'zugferd1')) {
      Object.assign(d, sub, { pdfBytes: d.bytes, pdfAttachments: info.attachments, xmlName: a.name });
      d.label = 'PDF · ' + sub.label;
      return;
    }
  }
  d.label = 'PDF';
  d.verdict = VD.verdictPlainPdf({ attachments: info.attachments, encrypted: info.encrypted });
  d.status = 'done';
}

async function readImageDoc(d) {
  let results = [];
  try {
    const QR = await lib('qrDecode');
    results = await QR.fromFile(new Blob([d.bytes]), { formats: ['QRCode'] });
  } catch (e) {
    d.verdict = { tone: 'warn', title: { key: 't_qr_engine', params: {} }, reasons: [] };
    d.status = 'done';
    return;
  }
  const text = (results[0] && results[0].text) || '';
  d.label = 'QR';
  readQrText(d, text, results.length);
}

function readQrText(d, text, found = 1) {
  d.qrText = text;
  if (!text) {
    d.verdict = { tone: 'warn', title: { key: 't_no_qr', params: {} }, reasons: [{ key: found ? 'qr_empty' : 'no_qr_found', params: {} }] };
    d.status = 'done';
    return;
  }
  if (/^https?:\/\/.*(nfce|fazenda|sefaz)/i.test(text) && /p=\d{44}/.test(text)) {
    const ch = /p=(\d{44})/.exec(text)[1];
    d.label = 'NFC-e QR';
    const c = N.parseChave(ch);
    d.verdict = { tone: c.ok ? 'info' : 'bad', title: { key: 't_nfce_qr', params: { chave: chaveGroups(ch) } }, reasons: [{ key: c.ok ? 'nfce_qr_ok' : 'nfe_chave_dv', params: { url: text.split('?')[0] } }] };
    d.status = 'done';
    return;
  }
  const qr = Z.decodeTlv(text);
  if (!qr.error && qr.tags[1] && qr.tags[2]) {
    qr.problems = Z.checkPhase1(qr);
    d.label = 'ZATCA QR';
    d.zatca = { qr, qrText: text };
    d.verdict = VD.verdictZatca({ qr, xml: null });
    d.status = 'done';
    if (qr.tags[7] && qr.tags[8]) {
      zatcaSignature(qr).then((ok) => { qr.sigOk = ok; d.verdict = VD.verdictZatca({ qr, xml: null }); update(d); });
    }
    return;
  }
  d.verdict = { tone: 'info', title: { key: 't_qr_other', params: {} }, reasons: [{ key: 'qr_other', params: { text: text.slice(0, 160) } }] };
  d.status = 'done';
}

async function readLeaf(d) {
  try {
    if (d.kind === 'xml') await readXmlDoc(d, d.bytes, null);
    else if (d.kind === 'pdf') await readPdfDoc(d);
    else if (d.kind === 'image') await readImageDoc(d);
    else if (d.kind === 'qrtext') readQrText(d, new TextDecoder().decode(d.bytes).trim());
    else if (d.kind === 'chave') {
      const raw = new TextDecoder().decode(d.bytes).trim();
      const c = N.parseChave(raw);
      d.label = 'Chave NF-e';
      d.verdict = { tone: c.ok ? 'info' : 'bad', title: { key: c.ok ? 't_chave_ok' : 't_chave_bad', params: { chave: chaveGroups(c.digits) } }, reasons: c.ok ? [{ key: 'chave_parts', params: { uf: N.UF_CODES[+c.parts.cUF] || c.parts.cUF, aamm: `${c.parts.aamm.slice(2)}/20${c.parts.aamm.slice(0, 2)}`, cnpj: brDoc(c.parts.doc), mod: c.parts.mod, serie: +c.parts.serie, nnf: +c.parts.nNF } }, { key: 'chave_no_download', params: {} }] : c.problems.map((p) => ({ key: 'nfe_' + p, params: {} })) };
      d.status = 'done';
    } else if (d.kind === 'der' || d.kind === 'pem' || /\.(sig|sgn|p7s|p7m)$/i.test(d.name)) {
      try { d.cms = cmsCertificates(d.bytes); } catch (_) { d.cms = null; }
      d.label = 'CMS';
      d.verdict = { tone: 'info', title: { key: 't_signature_file', params: {} }, reasons: d.cms && d.cms.certs[0] ? [{ key: 'sig_cert', params: { who: d.cms.certs[0].cn || d.cms.certs[0].org, issuer: d.cms.certs[0].issuerCn } }, { key: 'sig_check_elsewhere', params: {} }] : [{ key: 'sig_unreadable', params: {} }] };
      d.status = 'done';
    } else {
      // The sniffer's own word ("unknown", "url") is not for people: name the file type by its extension.
      const ext = (/\.([a-z0-9]{1,8})$/i.exec(d.name || '') || [])[1];
      d.label = ext ? ext.toUpperCase() : (d.kind === 'unknown' ? '—' : d.kind.toUpperCase());
      // Named .xml but not XML inside (binary, damaged): a broken invoice file, not "another file type".
      if (/^xml$/i.test(ext || '')) d.verdict = { tone: 'bad', title: { key: 't_xml_broken', params: {} }, reasons: [{ key: 'xml_not_text', params: {} }] };
      else d.verdict = { tone: 'info', title: { key: 't_not_invoice', params: { name: d.name } }, reasons: [{ key: 'not_invoice_why', params: { kind: d.label } }] };
      d.status = 'done';
    }
  } catch (e) {
    d.status = 'done';
    d.verdict = { tone: 'bad', title: { key: 't_read_failed', params: {} }, reasons: [] };
    try { window.__handleError && window.__handleError(e, 'e_invoice_read', { say: ui('read_failed') }); } catch (_) {}
  }
}

/* An NF-e and its events can arrive in one ZIP: a cancelamento changes the verdict. */
function refreshNfeVerdicts() {
  const events = S.docs.filter((x) => x.event).map((x) => x.event);
  for (const d of S.docs) if (d.nfe) { d.verdict = VD.verdictNfe({ model: d.nfe, check: d.nfeCheck, sig: d.nfeSig || null, events, lote: d.lote }); update(d); }
}

/* УПД + its .sig in the same drop: pair by name. */
function pairSignatures() {
  for (const d of S.docs) {
    if (!d.cls || d.cls.family !== 'upd') continue;
    const base = d.name.replace(/\.xml$/i, '');
    const sig = S.docs.find((x) => x.cms && x !== d && x.name.toLowerCase().startsWith(base.toLowerCase()));
    if (sig) { d.sigDoc = sig; d.verdict = VD.verdictUpd({ cls: d.cls, model: d.upd, cms: sig.cms }); if (d.encBad) d.verdict = { tone: 'bad', title: { key: 't_xml_encoding', params: { name: d.encBad } }, reasons: [{ key: 'xml_encoding_why', params: { name: d.encBad } }, ...d.verdict.reasons] }; sig.pairedWith = d; }
  }
}

/* ── validation queue (main thread: SaxonJS needs a DOM) ───────────── */
let vq = Promise.resolve();
function validate(d, opts = {}) {
  if (!d.scenario) return;
  prefetch(d.scenario);
  vq = vq.then(async () => {
    // A file that left the page (cleared, another drop) is not checked any more:
    // a 2 MB invoice in the queue held the next small one for a minute.
    if (!S.docs.includes(d)) return;
    try {
      const r = await runScenario(d.xmlText, d.scenario, { force: !!opts.force });
      d.findings = r.findings;
      d.validationMs = r.ms;
      d.xsd = r.xsd;
      d.rulesSkipped = r.rulesSkipped || null;
      ae('einvoice_validated', { scenario: d.scenario.key, ms: r.ms, err: r.findings.filter((f) => f.level === 'error').length, warn: r.findings.filter((f) => f.level === 'warning').length, xsd: r.xsd ? (r.xsd.ran ? (r.xsd.valid ? 'valid' : 'invalid') : r.xsd.code) : 'none', xsd_ms: r.xsd ? r.xsd.ms : 0 });
    } catch (e) {
      d.findings = null;
      d.validationError = e.code || 'TRANSFORM';
      ae('einvoice_validation_failed', { scenario: d.scenario.key, code: d.validationError, msg: String(e && e.message || '').slice(0, 120) });
    }
    d.verdict = VD.verdictEn16931({ cls: d.cls, model: d.model, scenario: d.scenario, findings: d.findings, container: d.container, xsd: d.xsd, rulesSkipped: d.rulesSkipped, validationError: d.validationError ? ui('validation_' + d.validationError.toLowerCase()) : '', encodingBad: d.encBad });
    d.status = 'done';
    reportVerdict(d);
    update(d);
    await new Promise((r) => setTimeout(r, 0));
  });
}

/* ── intake ────────────────────────────────────────────────────────── */

async function takeFiles(files, { append = false, sample = false } = {}) {
  if (!files || !files.length) return;
  if (!append) { S.docs = []; S.cur = -1; S.filter = 'all'; }
  // A newer drop owns the page: the older one stops reading and never opens a file.
  const gen = ++S.gen;
  stage('file_chosen', { n: files.length });
  $('ei-empty').hidden = true;
  setBusy(+1);
  try {
    const inputs = [];
    for (const f of files) inputs.push({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()), file: f });
    let JSZip = null;
    if (inputs.some((x) => sniff(x.bytes, x.name) === 'zip' || sniff(x.bytes, x.name) === 'eml')) { try { JSZip = await lib('jszip'); } catch (_) {} }
    for (const inp of inputs) {
      const leaves = await unpack([inp], { JSZip });
      for (const leaf of leaves) {
        if (/^(zip-|eml-)/.test(leaf.kind)) {
          const d = newDoc(leaf, inp.file);
          d.label = leaf.kind.startsWith('zip') ? 'ZIP' : 'EML';
          // A broken archive or mail is something to act on; an empty one is just a fact.
          d.verdict = { tone: /broken|unread/.test(leaf.kind) ? 'warn' : 'info', title: { key: 't_' + leaf.kind.replace(/-/g, '_'), params: { name: leaf.name } }, reasons: [] };
          d.status = 'done';
          S.docs.push(d);
          continue;
        }
        const d = newDoc(leaf, inp.file);
        // Our own example is a demonstration, not the person's file: it never bills.
        if (sample) d.billed = true;
        S.docs.push(d);
      }
    }
    renderAll();
    for (const d of S.docs) {
      if (gen !== S.gen) return;
      if (d.status !== 'reading') continue;
      await readLeaf(d);
      update(d);
      if (d.status === 'checking' && d.scenario) validate(d);
      else if (d.status === 'done') reportVerdict(d);
      await new Promise((r) => setTimeout(r, 0));
    }
    if (gen !== S.gen) return;
    pairSignatures();
    refreshNfeVerdicts();
    markDuplicates();
    renderAll();
    if (S.docs.length === 1) open(0);
    else if (S.docs.length > 1) {
      // One line per batch, when the last rule set has answered: sizes and answers, no names.
      const docs = S.docs, n = files.length;
      vq.then(() => {
        if (gen !== S.gen) return;
        const c = { ok: 0, warn: 0, bad: 0, info: 0 };
        for (const d of docs) if (d.verdict) c[d.verdict.tone] = (c[d.verdict.tone] || 0) + 1;
        const fmts = {};
        for (const d of docs) { const k = d.cls ? d.cls.family : d.kind; fmts[k] = (fmts[k] || 0) + 1; }
        ae('einvoice_batch', { files: n, docs: docs.length, ...c, nested: docs.filter((d) => d.path && d.path !== d.name).length, dup: docs.filter((d) => d.dupOf).length, formats: Object.entries(fmts).map(([k, v]) => k + ':' + v).join(',').slice(0, 120) });
      });
    }
  } finally {
    setBusy(-1);
  }
}

function setBusy(delta) {
  S.busy = Math.max(0, S.busy + delta);
  const b = $('ei-busy');
  if (b) b.hidden = !S.busy;
}

/* ── rendering: list ───────────────────────────────────────────────── */

const TONE_ICON = { ok: '✓', warn: '!', bad: '✕', info: 'i' };

function summaryOf(d) {
  if (d.model) {
    const m = d.model;
    return { number: m.id, date: m.issueDate, seller: m.seller && m.seller.name, buyer: m.buyer && m.buyer.name, total: m.totals && (m.totals.payable || m.totals.taxInclusive), currency: m.currency, due: m.dueDate };
  }
  if (d.nfe) {
    const m = d.nfe;
    return { number: `${m.ide.nNF} / ${m.ide.serie}`, date: (m.ide.dhEmi || '').slice(0, 10), seller: m.emit && m.emit.name, buyer: m.dest && m.dest.name, total: m.totals.vNF, currency: 'BRL', due: (m.billing.installments[0] || {}).due };
  }
  if (d.upd) {
    const m = d.upd;
    return { number: m.number, date: m.dateIso, seller: m.seller && m.seller.name, buyer: m.buyer && m.buyer.name, total: m.totals && m.totals.withVat, currency: m.currency || 'RUB' };
  }
  if (d.zatca && d.zatca.qr && !d.zatca.qr.error) {
    const q = d.zatca.qr.tags;
    return { number: '', date: q[3] ? q[3].text.slice(0, 10) : '', seller: q[1] && q[1].text, buyer: '', total: q[4] && q[4].text, currency: 'SAR' };
  }
  return {};
}

/* Same number from the same seller twice in one drop: the portal copy and the
   e-mail copy of one invoice — paying both is the accountant's classic loss. */
function markDuplicates() {
  const seen = new Map();
  for (const d of S.docs) {
    d.dupOf = null;
    const s = summaryOf(d);
    if (!s.number || !s.seller) continue;
    const k = (s.number + '|' + s.seller + '|' + (s.total || '')).toLowerCase();
    if (seen.has(k)) d.dupOf = seen.get(k); else seen.set(k, d);
  }
}

const isProblem = (d) => !!(d.verdict && (d.verdict.tone === 'bad' || d.verdict.tone === 'warn')) || !!d.dupOf;
const isValid = (d) => !!(d.verdict && d.verdict.tone === 'ok') && !d.dupOf;
const shown = (d) => S.filter === 'all' || (S.filter === 'bad' ? isProblem(d) : isValid(d));

function renderAll() {
  const list = $('ei-list');
  const many = S.docs.length > 1;
  $('ei-batch').hidden = !many;
  if (many) {
    list.textContent = '';
    S.docs.forEach((d, i) => { if (shown(d)) list.append(listRow(d, i)); });
    batchHead();
  }
  layout();
  if (S.cur >= 0) renderDetail(S.docs[S.cur]);
  else $('ei-detail').hidden = true;
}

/* Wide screens: the list stays beside the open file (master–detail), so the
   accountant walks through a month of invoices without scrolling back. The
   switch is CSS on the container width (.ei-split); here only the state. */
function layout() {
  const w = $('ei-work');
  if (w) w.classList.toggle('ei-split', S.docs.length > 1 && S.cur >= 0);
}

function batchHead() {
  const bad = S.docs.filter(isProblem).length;
  $('ei-batch-count').textContent = ui('batch_count', { n: S.docs.length, bad: S.docs.filter((d) => d.verdict && d.verdict.tone === 'bad').length });
  const f = $('ei-filter');
  if (f) {
    f.querySelectorAll('[data-filter]').forEach((b) => {
      const on = b.dataset.filter === S.filter;
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      const n = b.dataset.filter === 'all' ? S.docs.length : b.dataset.filter === 'bad' ? bad : S.docs.filter(isValid).length;
      const c = b.querySelector('.ei-fn');
      if (c) c.textContent = String(n);
    });
  }
  // Totals per currency, for the files that carry an amount — the sum the
  // accountant checks against the bank statement.
  const sums = new Map();
  for (const d of S.docs) {
    if (d.dupOf) continue;
    const sm = summaryOf(d);
    if (!sm.total || !isFinite(+sm.total)) continue;
    const c = sm.currency || '';
    sums.set(c, (sums.get(c) || 0) + +sm.total);
  }
  const tot = $('ei-batch-total');
  if (tot) {
    tot.textContent = sums.size ? ui('batch_total', { sum: [...sums].map(([c, v]) => money(v.toFixed(2), c)).join(' · ') }) : '';
    tot.hidden = !sums.size;
  }
}

function listRow(d, i) {
  const s = summaryOf(d);
  const tone = d.verdict ? d.verdict.tone : 'info';
  const dup = d.dupOf ? h('div', { class: 'ei-small ei-dup' }, ui('dup_of', { name: d.dupOf.name })) : null;
  return h('tr', { class: 'ei-row ei-tone-' + tone + (S.cur === i ? ' on' : ''), 'data-uid': String(d.uid), 'aria-current': S.cur === i ? 'true' : null },
    h('td', null, h('span', { class: 'ei-dot ei-tone-' + tone, 'aria-hidden': 'true' }, TONE_ICON[tone]),
      h('button', { type: 'button', class: 'ei-open', 'data-i': String(i), onclick: () => open(i) }, d.name),
      d.path && d.path !== d.name ? h('div', { class: 'ei-small' }, d.path) : null),
    h('td', null, d.label || '', h('div', { class: 'ei-small' }, d.status === 'reading' || d.status === 'checking' ? ui('checking') : vtext(d.verdict && d.verdict.title)), dup),
    h('td', null, bidi(s.number || '')),
    h('td', null, bidi(s.seller || '')),
    h('td', { class: 'num' }, s.total ? money(s.total, s.currency) : ''),
  );
}

function update(d) {
  if (S.docs.length > 1) {
    const row = document.querySelector(`#ei-list tr[data-uid="${d.uid}"]`);
    if (row) row.replaceWith(listRow(d, S.docs.indexOf(d)));
    batchHead();
  }
  if (S.cur >= 0 && S.docs[S.cur] === d) renderDetail(d);
}

/* ── rendering: one document ───────────────────────────────────────── */

function open(i) {
  const prev = S.cur;
  S.cur = i;
  const d = S.docs[i];
  // The selected row, without redrawing a list of fifty.
  document.querySelectorAll('#ei-list tr.on').forEach((r) => { r.classList.remove('on'); r.removeAttribute('aria-current'); });
  const row = document.querySelector(`#ei-list tr[data-uid="${d.uid}"]`);
  if (row) { row.classList.add('on'); row.setAttribute('aria-current', 'true'); }
  layout();
  // The full view is the delivered result: one unit per file per day.
  if (!d.billed && window.__toolLimit && hasFullView(d)) {
    const run = window.__toolLimit.begin(SLUG, d.key);
    if (!run) { d.walled = true; renderDetail(d); return; }
    run.commit();
    d.billed = true;
    d.walled = false;
    ae('einvoice_opened', { format: d.cls ? d.cls.format : d.kind, src: S.docs.length > 1 ? 'batch' : 'single' });
  }
  renderDetail(d);
  const det = $('ei-detail');
  // Beside the list (wide screen) the file opens in place; under it (phone) the
  // page brings the file into view — only as far as needed.
  if (det && S.docs.length > 1 && prev !== i && !splitShown()) det.scrollIntoView({ block: 'nearest' });
}

function splitShown() {
  const w = $('ei-work');
  return !!(w && w.classList.contains('ei-split') && getComputedStyle(w).display === 'grid');
}

function hasFullView(d) { return !!(d.model || d.nfe || d.upd || d.zatca); }

function renderDetail(d) {
  const det = $('ei-detail');
  det.hidden = false;
  $('ei-back').hidden = S.docs.length < 2;
  // Answer.
  const v = d.verdict || { tone: 'info', title: { key: 't_checking' }, reasons: [] };
  const ans = $('ei-answer');
  ans.className = 'tc-answer ei-answer ei-tone-' + v.tone;
  $('ei-answer-v').textContent = vtext(v.title);
  const s = summaryOf(d);
  // Each part isolated: an invoice number or a Latin name inside the Arabic line keeps its order.
  const sub = $('ei-answer-sub');
  sub.textContent = '';
  [d.label, s.number && `№ ${s.number}`, s.seller && s.buyer ? `${s.seller} → ${s.buyer}` : s.seller, s.total ? money(s.total, s.currency) : '', s.due ? `${ui('due')} ${date(s.due)}` : '']
    .filter(Boolean).forEach((part, k) => { if (k) sub.append(' · '); sub.append(bidi(part)); });
  const rs = $('ei-reasons');
  rs.textContent = '';
  for (const r of v.reasons || []) rs.append(h('li', null, vtext(r)));
  if (d.dupOf) rs.append(h('li', { class: 'ei-dup' }, ui('dup_reason', { name: d.dupOf.path || d.dupOf.name })));
  $('ei-checking').hidden = d.status !== 'checking';
  // Actions.
  $('ei-act-pdf').hidden = !(d.model || d.nfe || d.upd);
  $('ei-act-xml').hidden = !d.xmlText;
  const sigBtn = $('ei-act-sig');
  if (sigBtn) sigBtn.hidden = !(d.cls && d.cls.family === 'upd') && !d.cms;
  $('ei-wall-note').hidden = !d.walled;
  // A big invoice whose rules wait for the person's own go.
  const rr = $('ei-run-rules');
  if (rr) rr.hidden = !(d.rulesSkipped && d.rulesSkipped.canForce && !d.walled && d.status === 'done');
  // A rejected invoice: the sender needs the reasons in words — and the person may
  // want to correct it and issue it again (or in another format).
  const note = $('ei-act-note');
  if (note) note.hidden = d.walled || !(d.verdict && d.verdict.tone === 'bad' && (d.model || d.nfe || d.upd) && d.status === 'done');
  const edit = $('ei-act-edit');
  if (edit) edit.hidden = d.walled || !d.model || !!(d.cls && d.cls.zatca) || d.status !== 'done';
  // Findings.
  renderFindings(d);
  // Views.
  renderViews(d);
}

async function renderFindings(d) {
  const box = $('ei-findings');
  const list = $('ei-findings-list');
  if (!d.findings || !d.findings.length || d.walled) {
    box.hidden = !(d.findings && d.findings.length === 0 && d.scenario && !d.walled && !d.rulesSkipped);
    list.textContent = '';
    if (!box.hidden) list.append(h('li', { class: 'ei-ok' }, ui('no_findings', { set: d.scenario.label, ms: d.validationMs || 0 })));
    return;
  }
  box.hidden = false;
  const L = await loadLocaleData();
  list.textContent = '';
  const groups = { error: [], warning: [], info: [] };
  d.findings.forEach((f, k) => groups[f.level].push([f, k]));
  for (const level of ['error', 'warning', 'info']) {
    if (!groups[level].length) continue;
    const head = h('li', { class: 'ei-fgroup ei-lv-' + level }, ui('lv_' + level, { n: groups[level].length }));
    list.append(head);
    // Hundreds of identical syntax hints would bury the one real error: fold repeats.
    // Schema messages share one id (XSD): each distinct message is its own finding.
    const foldKey = (f) => (f.kind === 'xsd' ? 'XSD|' + f.text : f.id);
    const seen = new Map();
    for (const [f] of groups[level]) seen.set(foldKey(f), (seen.get(foldKey(f)) || 0) + 1);
    const shown = new Set();
    for (const [f, k] of groups[level]) {
      if (shown.has(foldKey(f))) continue;
      shown.add(foldKey(f));
      const ex = explain(f, L);
      const count = seen.get(foldKey(f));
      const li = h('li', { class: 'ei-f ei-lv-' + level },
        h('div', { class: 'ei-f-h' },
          h('code', { class: 'ei-rule' }, f.id),
          count > 1 ? h('span', { class: 'ei-small' }, ui('times', { n: count })) : null,
          ex.fields.length ? h('button', { type: 'button', class: 'ei-show', onclick: () => showFinding(d, f, ex) }, ui('show_field')) : null,
        ),
        h('p', { class: 'ei-f-t' }, ex.text || f.text),
        ex.fix ? h('p', { class: 'ei-f-fix' }, ex.fix) : null,
        h('details', { class: 'ei-f-src' }, h('summary', null, ui('official_text')), h('p', null, f.text), h('p', { class: 'ei-mono ei-small' }, normLocation(f.location)), f.test ? h('p', { class: 'ei-mono ei-small' }, f.test) : null),
      );
      list.append(li);
    }
  }
}

function showFinding(d, f, ex) {
  setTab('doc');
  const root = $('ei-view-doc');
  const loc = normLocation(f.location);
  const lm = /\/(?:InvoiceLine|CreditNoteLine|IncludedSupplyChainTradeLineItem)\[(\d+)\]/.exec(loc);
  const target = markFields(root, ex.fields, lm ? +lm[1] - 1 : null);
  if (target) target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

let tab = 'doc';
function setTab(t) {
  tab = t;
  document.querySelectorAll('#ei-tabs [data-tab]').forEach((b) => { const on = b.dataset.tab === t; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
  document.querySelectorAll('.ei-view').forEach((v) => { v.hidden = v.id !== 'ei-view-' + t; });
  // The DANFE paginates by measuring its sheets, so it is drawn only once its view is shown.
  const d = S.docs[S.cur];
  if (t === 'danfe' && d && d.nfe && !d.walled) renderDanfePreview(d);
}

function renderViews(d) {
  const docView = $('ei-view-doc');
  docView.textContent = '';
  const tabs = { doc: false, danfe: false, xml: !!d.xmlText, att: false };
  if (d.walled) {
    docView.append(h('p', { class: 'tc-hint' }, ui('walled_hint')));
    tabs.doc = true;
  } else if (d.model) {
    docView.append(renderEn16931(d.model, { formatLabel: d.label, lang: CFG.lang }));
    tabs.doc = true;
    tabs.att = !!((d.model.attachments || []).some((a) => a.base64) || (d.pdfAttachments || []).length > 1);
  } else if (d.nfe) {
    docView.append(nfeSummary(d));
    tabs.doc = true;
    tabs.danfe = true;
  } else if (d.upd) {
    docView.append(renderUpd(d.upd, { screen: true }));
    tabs.doc = true;
  } else if (d.zatca) {
    docView.append(zatcaView(d));
    tabs.doc = true;
  }
  document.querySelectorAll('#ei-tabs [data-tab]').forEach((b) => { b.hidden = !tabs[b.dataset.tab]; });
  if (!tabs[tab]) setTab(tabs.doc ? 'doc' : tabs.xml ? 'xml' : 'doc');
  else setTab(tab);
  $('ei-views').hidden = !Object.values(tabs).some(Boolean);
  if (tabs.xml) renderXml(d);
  if (tabs.att) renderAttachments(d);
}

function nfeSummary(d) {
  const m = d.nfe;
  const p = m.protocol;
  const kv = (k, val) => (val ? h('div', { class: 'ei-f' }, h('span', { class: 'ei-fl' }, k), h('span', { class: 'ei-fv' }, val)) : null);
  return h('article', { class: 'ei-doc ei-nfe' },
    h('div', { class: 'ei-head' },
      h('div', { class: 'ei-title' }, h('p', { class: 'ei-kind' }, `${m.ide.mod === '65' ? 'NFC-e' : 'NF-e'} · ${m.ide.natOp || ''}`), h('h2', { class: 'ei-num' }, `${m.ide.nNF} / ${m.ide.serie}`)),
      h('div', { class: 'ei-dates' }, kv(ui('issue_date'), date(m.ide.dhEmi)), kv('Chave', chaveGroups(m.chave)), p ? kv(ui('protocol'), `${p.number} · ${p.cStat} ${p.reason}`) : null),
    ),
    h('div', { class: 'ei-parties' },
      h('section', { class: 'ei-party' }, h('h3', { class: 'ei-h' }, 'Emitente'), h('p', { class: 'ei-pname' }, m.emit.name), kv('CNPJ/CPF', brDoc(m.emit.cnpj || m.emit.cpf)), kv('IE', m.emit.ie)),
      h('section', { class: 'ei-party' }, h('h3', { class: 'ei-h' }, 'Destinatário'), h('p', { class: 'ei-pname' }, m.dest ? m.dest.name : ''), m.dest ? kv('CNPJ/CPF', brDoc(m.dest.cnpj || m.dest.cpf) || m.dest.foreignId) : null),
    ),
    h('table', { class: 'ei-lines' },
      h('thead', null, h('tr', null, h('th', null, '#'), h('th', null, ui('item')), h('th', { class: 'num' }, ui('qty')), h('th', { class: 'num' }, ui('price')), h('th', { class: 'num' }, ui('net')))),
      h('tbody', null, ...m.items.map((it) => h('tr', null, h('td', null, it.n), h('td', null, h('div', { class: 'ei-iname' }, it.name), h('div', { class: 'ei-small' }, `NCM ${it.ncm} · CFOP ${it.cfop}`)), h('td', { class: 'num' }, `${it.qty} ${it.unit}`), h('td', { class: 'num' }, money(it.unitPrice, 'BRL')), h('td', { class: 'num' }, money(it.total, 'BRL')))))),
    h('div', { class: 'ei-sum' }, h('div', { class: 'ei-totals' },
      h('div', { class: 'ei-tot' }, h('span', null, 'ICMS'), h('b', null, money(m.totals.vICMS, 'BRL'))),
      h('div', { class: 'ei-tot' }, h('span', null, 'IPI'), h('b', null, money(m.totals.vIPI, 'BRL'))),
      h('div', { class: 'ei-tot ei-tot-big' }, h('span', null, ui('t_nf')), h('b', null, money(m.totals.vNF, 'BRL'))),
    )),
  );
}

function zatcaView(d) {
  const q = d.zatca.qr;
  const rows = q && !q.error ? Z.describe(q) : [];
  return h('article', { class: 'ei-doc' },
    h('table', { class: 'ei-lines' },
      h('thead', null, h('tr', null, h('th', null, ui('qr_tag')), h('th', null, ui('qr_value')))),
      h('tbody', null, ...rows.map((r) => h('tr', null, h('td', null, `${r.tag} · ${ui('qr_tag_' + r.tag)}`), h('td', { class: r.hex ? 'ei-mono ei-small' : null }, r.text || (r.hex.length > 96 ? r.hex.slice(0, 96) + '…' : r.hex)))))),
    d.zatca.xml && d.zatca.xml.hash ? h('p', { class: 'ei-small ei-mono' }, `SHA-256: ${d.zatca.xml.hash}`) : null,
  );
}

let danfeLayout = 'a4';
function renderDanfePreview(d) {
  const host = $('ei-view-danfe-sheets');
  const wm = watermarkOf(d.nfe, d.verdict);
  if (!danfeLayout || (d.nfe.ide.mod === '65' && danfeLayout === 'a4')) danfeLayout = d.nfe.ide.mod === '65' ? 'nfce' : 'a4';
  document.querySelectorAll('#ei-danfe-layout [data-layout]').forEach((b) => { const on = b.dataset.layout === danfeLayout; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
  const draw = () => {
    host.textContent = '';
    if (danfeLayout === 'a4') renderDanfeA4(d.nfe, host, { watermark: wm });
    else if (danfeLayout === 'nfce') host.append(renderDanfeNfce(d.nfe, { watermark: wm }));
    else host.append(renderDanfeLabel(d.nfe, { watermark: wm }));
    fitSheets(host);
  };
  if (danfeLayout === 'nfce' && d.nfe.qrCode && !window.qrcode) lib('qrGen').then(draw, draw); else draw();
}

/* Sheets are drawn at their real size (mm) — the pagination measures them —
   and only scaled for the eye. */
function fitSheets(host) {
  const w = host.parentElement.clientWidth || 600;
  host.querySelectorAll('.hp-page').forEach((s) => {
    const sw = s.offsetWidth || 1;
    const k = Math.min(1, (w - 8) / sw);
    s.style.transform = k < 1 ? `scale(${k})` : '';
    s.style.transformOrigin = 'top left';
    s.style.marginBottom = k < 1 ? `${-(s.offsetHeight * (1 - k)) + 12}px` : '12px';
  });
}

function renderXml(d) {
  const pre = $('ei-xml');
  const max = 400000;
  const text = d.xmlText.length > max ? d.xmlText.slice(0, max) + '\n…' : d.xmlText;
  pre.textContent = text;
}

function renderAttachments(d) {
  const ul = $('ei-att-list');
  ul.textContent = '';
  const items = [];
  for (const a of (d.model && d.model.attachments) || []) if (a.base64) items.push({ name: a.filename || `${a.id || 'attachment'}`, mime: a.mime, get: () => Z.b64ToBytes(a.base64), desc: a.desc });
  for (const a of d.pdfAttachments || []) if (a.name !== d.xmlName) items.push({ name: a.name, mime: a.mime, get: () => a.bytes, desc: a.desc });
  // одна кнопка — одно вложение на её собственный клик (не пачка)
  const row = (it) => h('li', null, h('button', { type: 'button', class: 'tc-btn sm ghost', onclick: () => window.__downloadFile(new Blob([it.get()], { type: it.mime || 'application/octet-stream' }), it.name) }, `⬇ ${it.name}`), it.desc ? h('span', { class: 'ei-small' }, ` ${it.desc}`) : null);
  for (const it of items) {
    ul.append(row(it));
  }
}

/* ── downloads ─────────────────────────────────────────────────────── */

function ensureBilled(d) {
  if (d.billed || !window.__toolLimit) return true;
  const run = window.__toolLimit.begin(SLUG, d.key);
  if (!run) return false;
  run.commit();
  d.billed = true;
  return true;
}

function baseName(d) {
  const s = summaryOf(d);
  const n = (s.number || d.name.replace(/\.[^.]+$/, '')).replace(/[\\/:*?"<>|]+/g, '-').slice(0, 80);
  return n || 'invoice';
}

async function pdfOf(d) {
  if (!window.HtmlPdf) throw Object.assign(new Error('htmlpdf_missing'), { code: 'HTMLPDF' });
  window.HtmlPdf.installFonts && window.HtmlPdf.installFonts();
  const host = $('ei-print');
  host.textContent = '';
  host.hidden = false;
  try {
    if (d.nfe) {
      const wm = watermarkOf(d.nfe, d.verdict);
      if (danfeLayout === 'a4') {
        renderDanfeA4(d.nfe, host, { watermark: wm });
        return await window.HtmlPdf.fromElement(host, { pages: 'css', page: 'a4', title: `DANFE ${d.nfe.ide.nNF}`, lang: 'pt-BR' });
      }
      if (danfeLayout === 'nfce' && d.nfe.qrCode) await lib('qrGen');
      const sheet = danfeLayout === 'nfce' ? renderDanfeNfce(d.nfe, { watermark: wm }) : renderDanfeLabel(d.nfe, { watermark: wm });
      host.append(sheet);
      return await window.HtmlPdf.fromElement(sheet, { pages: 'single', page: danfeLayout === 'label' ? { wMm: 100, hMm: 150 } : undefined, title: `DANFE ${d.nfe.ide.nNF}`, lang: 'pt-BR' });
    }
    if (d.upd) {
      const sheet = renderUpd(d.upd, { screen: false });
      host.append(sheet);
      return await window.HtmlPdf.fromElement(sheet, { pages: 'auto', page: 'a4', orientation: 'landscape', marginsMm: { t: 8, r: 8, b: 10, l: 8 }, title: `УПД ${d.upd.number || ''}`, lang: 'ru' });
    }
    const col = h('div', { class: 'ei-sheet' }, renderEn16931(d.model, { formatLabel: d.label, forPdf: true, lang: CFG.lang }));
    host.append(col);
    const xmlBytes = new TextEncoder().encode(d.xmlText);
    return await window.HtmlPdf.fromElement(col, {
      pages: 'auto', page: 'a4', marginsMm: { t: 12, r: 12, b: 14, l: 12 },
      title: `${d.model.id || ''} ${d.model.seller ? d.model.seller.name : ''}`.trim(), lang: CFG.lang, rtl: CFG.rtl,
      footer: (p, n) => `${d.model.id || ''} · ${p}/${n}`,
      pdfa: '3b',
      attachments: [{ name: d.xmlName || (d.name.match(/\.xml$/i) ? d.name : 'invoice.xml'), bytes: xmlBytes, mime: 'text/xml', afRelationship: 'Source', description: d.label }],
    });
  } finally {
    host.textContent = '';
    host.hidden = true;
  }
}

async function downloadPdf() {
  const d = S.docs[S.cur];
  if (!d || !ensureBilled(d)) return;
  const btn = $('ei-act-pdf');
  btn.disabled = true;
  try {
    const bytes = await pdfOf(d);
    const name = (d.nfe ? `DANFE-${d.nfe.chave}` : baseName(d)) + '.pdf';
    await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), name);
    ae('einvoice_pdf', { format: d.cls ? d.cls.format : '', layout: d.nfe ? danfeLayout : 'a4' });
  } catch (e) {
    window.__handleError ? window.__handleError(e, 'e_invoice_pdf', { say: ui(e.code === 'HTMLPDF' ? 'pdf_engine_missing' : 'pdf_failed') }) : console.error(e);
  } finally { btn.disabled = false; }
}

function downloadXml() {
  const d = S.docs[S.cur];
  if (!d || !d.xmlText || !ensureBilled(d)) return;
  window.__downloadFile(new Blob([d.xmlText], { type: 'application/xml' }), d.xmlName || (d.name.match(/\.xml$/i) ? d.name : baseName(d) + '.xml'));
}

/* CSV for the accountant: separators follow the language's decimal comma. */
function csvOpts() {
  const dec = (1.5).toLocaleString(CFG.lang || 'en').includes(',') ? ',' : '.';
  return { sep: dec === ',' ? ';' : ',', dec };
}
function csvCell(v, o) {
  if (v == null) return '';
  let s = String(v);
  if (/^-?\d+(\.\d+)?$/.test(s) && o.dec === ',') s = s.replace('.', ',');
  return /[";\n,\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(rows) {
  const o = csvOpts();
  return '\uFEFF' + rows.map((r) => r.map((c) => csvCell(c, o)).join(o.sep)).join('\r\n') + '\r\n';
}

function summaryCsv(docs) {
  const head = [ui('csv_file'), ui('csv_format'), ui('csv_verdict'), ui('csv_errors'), ui('csv_warnings'), ui('csv_number'), ui('csv_date'), ui('csv_due'), ui('csv_seller'), ui('csv_seller_id'), ui('csv_buyer'), ui('csv_buyer_id'), ui('csv_currency'), ui('csv_net'), ui('csv_vat'), ui('csv_gross'), ui('csv_payable'), ui('csv_key')];
  const rows = [head];
  for (const d of docs) {
    const s = summaryOf(d);
    const f = d.findings || [];
    let sellerId = '', buyerId = '', net = '', vat = '', gross = '', payable = s.total || '', key = '';
    if (d.model) { const m = d.model; sellerId = m.seller && (m.seller.vatId || (m.seller.legalId && m.seller.legalId.id)) || ''; buyerId = m.buyer && (m.buyer.vatId || (m.buyer.legalId && m.buyer.legalId.id)) || ''; net = m.totals.taxExclusive; vat = m.totals.vat; gross = m.totals.taxInclusive; payable = m.totals.payable; }
    if (d.nfe) { const m = d.nfe; sellerId = m.emit.cnpj || m.emit.cpf; buyerId = m.dest ? m.dest.cnpj || m.dest.cpf || m.dest.foreignId : ''; net = m.totals.vProd; vat = (+(m.totals.vICMS || 0) + +(m.totals.vIPI || 0)).toFixed(2); gross = m.totals.vNF; payable = m.totals.vNF; key = m.chave; }
    if (d.upd) { const m = d.upd; sellerId = m.seller ? m.seller.inn : ''; buyerId = m.buyer ? m.buyer.inn : ''; net = m.totals.withoutVat; vat = m.totals.vat; gross = m.totals.withVat; payable = m.totals.withVat; }
    rows.push([d.path || d.name, d.label, vtext(d.verdict && d.verdict.title), f.filter((x) => x.level === 'error').length, f.filter((x) => x.level === 'warning').length, s.number, s.date, s.due, s.seller, sellerId, s.buyer, buyerId, s.currency, net, vat, gross, payable, key]);
  }
  return csv(rows);
}

function linesCsv(docs) {
  const rows = [[ui('csv_file'), ui('csv_number'), ui('csv_line'), ui('csv_item'), ui('csv_qty'), ui('csv_unit'), ui('csv_price'), ui('csv_vat_cat'), ui('csv_vat_rate'), ui('csv_line_net')]];
  for (const d of docs) {
    const s = summaryOf(d);
    if (d.model) for (const l of d.model.lines) rows.push([d.path || d.name, s.number, l.id, l.item.name, l.qty, l.unit, l.price.net, l.vat.cat, l.vat.rate, l.net]);
    if (d.nfe) for (const it of d.nfe.items) rows.push([d.path || d.name, s.number, it.n, it.name, it.qty, it.unit, it.unitPrice, it.cst, it.icmsRate, it.total]);
    if (d.upd) for (const it of d.upd.lines) rows.push([d.path || d.name, s.number, it.n, it.name, it.qty, it.unit, it.price, '', it.rate, it.withoutVat]);
  }
  return csv(rows);
}

async function batchDownload(kind) {
  const docs = S.docs.filter((d) => hasFullView(d));
  if (!docs.length) return;
  const keys = docs.filter((d) => !d.billed).map((d) => d.key);
  let run = null;
  if (keys.length && window.__toolLimit) {
    run = window.__toolLimit.begin(SLUG, keys);
    if (!run) return;
  }
  const stamp = window.__localYmd();
  let via = 'direct';
  try {
    if (kind === 'csv') {
      await window.__downloadFile(new Blob([summaryCsv(docs)], { type: 'text/csv;charset=utf-8' }), `e-invoices-${stamp}.csv`);
    } else if (kind === 'lines') {
      await window.__downloadFile(new Blob([linesCsv(docs)], { type: 'text/csv;charset=utf-8' }), `e-invoice-lines-${stamp}.csv`);
    } else {
      // примитив приходит тегом страницы; запасной путь — по требованию (активация к тому времени может уйти)
      const BD = window.__batchDeliver || await lib('batch');
      const batch = await BD.openBatch({ zipName: `e-invoices-${stamp}.zip`, slug: SLUG, total: docs.length + 1, onProgress: () => {} });
      const prog = $('ei-batch-progress');
      prog.hidden = false;
      let k = 0;
      for (const d of docs) {
        prog.textContent = ui('batch_pdf_progress', { k: ++k, n: docs.length });
        try {
          const bytes = await pdfOf(d);
          await batch.write((d.nfe ? `DANFE-${d.nfe.chave}` : baseName(d)) + '.pdf', new Blob([bytes], { type: 'application/pdf' }));
        } catch (e) { ae('einvoice_batch_pdf_failed', { msg: String(e && e.message || '').slice(0, 80) }); }
      }
      await batch.write(`e-invoices-${stamp}.csv`, new Blob([summaryCsv(docs)], { type: 'text/csv;charset=utf-8' }));
      const fin = await batch.finish();
      prog.hidden = true;
      via = (fin && fin.via) || batch.mode || '';
    }
    ae('einvoice_batch_download', { kind, n: docs.length, billed: keys.length, via });
    docs.forEach((d) => { d.billed = true; });
    if (run) run.commit();
  } catch (e) {
    if (run) run.abort();
    if (!(window.__isUserCancel && window.__isUserCancel(e))) window.__handleError ? window.__handleError(e, 'e_invoice_batch', { say: ui('batch_failed') }) : console.error(e);
  }
}

/* ── telemetry: one answer per file, no names, no amounts ──────────── */
function reportVerdict(d) {
  if (d.reported || !d.verdict || d.status !== 'done') return;
  d.reported = true;
  const f = d.findings || [];
  ae('einvoice_verdict', {
    format: d.cls ? d.cls.format : d.kind,
    profile: d.cls && d.cls.profile ? d.cls.profile.key || '' : '',
    tone: d.verdict.tone,
    title: d.verdict.title ? d.verdict.title.key : '',
    err: f.filter((x) => x.level === 'error').length,
    warn: f.filter((x) => x.level === 'warning').length,
    // The first failing rule ids say WHICH mistakes people bring; ids only.
    rules: [...new Set(f.filter((x) => x.level === 'error').map((x) => x.id))].slice(0, 5).join(','),
    xsd: d.xsd ? (d.xsd.ran ? (d.xsd.valid ? 'valid' : 'invalid') : 'notrun') : '',
    run: d.rulesSkipped ? (d.rulesSkipped.canForce ? 'deferred' : 'skipped') : (d.findings ? 'ran' : 'none'),
    box: d.container ? d.container.kind : (d.path && d.path !== d.name ? 'archive' : 'file'),
    src: S.docs.length > 1 ? 'batch' : 'single',
    kb: Math.round(((d.bytes && d.bytes.length) || 0) / 1024),
    ms: d.validationMs || 0,
  });
}

/* ── a note for the sender: why the invoice will be rejected, in words ── */
async function senderNote(d) {
  const L = await loadLocaleData();
  const s = summaryOf(d);
  const out = [ui('note_head', { number: s.number || d.name, seller: s.seller || '' }).trim(), ''];
  const seen = new Set();
  const push = (line) => { if (line && !seen.has(line)) { seen.add(line); out.push('• ' + line); } };
  for (const r of (d.verdict && d.verdict.reasons) || []) push(vtext(r));
  for (const f of (d.findings || []).filter((x) => x.level === 'error')) {
    const ex = explain(f, L);
    push(`${ex.text || f.text} (${f.id})`);
  }
  out.push('', ui('note_tail', { format: d.scenario ? d.scenario.label : d.label || '' }));
  return out.join('\n');
}

async function copyNote() {
  const d = S.docs[S.cur];
  if (!d) return;
  try {
    const text = await senderNote(d);
    await navigator.clipboard.writeText(text);
    if (window.__toast) window.__toast(ui('note_copied'), 'success');
    ae('feature_use', { f: 'einvoice_sender_note', n: text.split('\n').length });
  } catch (e) {
    if (window.__handleError) window.__handleError(e, 'e_invoice_note', { say: ui('note_failed') });
  }
}

/* ── open → issue: correct a rejected invoice or re-issue it in another format ── */
let createApi = null;
function editInForm() {
  const d = S.docs[S.cur];
  if (!d || !d.model) return;
  const target = d.cls && d.cls.format === 'cii' ? (/xrechnung/.test(d.cls.profile && d.cls.profile.key || '') ? 'xr-cii' : 'facturx') : (/peppol/.test(d.cls && d.cls.customization || '') ? 'peppol' : 'xr-ubl');
  setMode('create');
  if (createApi) createApi.loadModel(d.model, { target, label: d.label });
  ae('feature_use', { f: 'einvoice_edit_in_form', format: d.cls ? d.cls.format : '', target });
  const pane = $('ei-create-pane');
  if (pane) pane.scrollIntoView({ block: 'nearest' });
}

function runRulesNow() {
  const d = S.docs[S.cur];
  if (!d || !d.rulesSkipped || !d.rulesSkipped.canForce) return;
  ae('feature_use', { f: 'einvoice_rules_forced', kb: Math.round(d.xmlText.length / 1024), lines: d.model && d.model.lines ? d.model.lines.length : 0 });
  d.rulesSkipped = null;
  d.findings = null;
  d.status = 'checking';
  d.reported = false;
  d.verdict = VD.verdictEn16931({ cls: d.cls, model: d.model, scenario: d.scenario, findings: null, container: d.container, encodingBad: d.encBad });
  renderDetail(d);
  // Paint «checking» before the rule engine takes the thread.
  requestAnimationFrame(() => setTimeout(() => validate(d, { force: true }), 30));
}

/* ── paste box ─────────────────────────────────────────────────────── */
function pasted() {
  const ta = $('ei-paste');
  const text = (ta.value || '').trim();
  if (!text) return;
  const bytes = new TextEncoder().encode(text);
  const kind = sniff(bytes, 'pasted.txt');
  const name = kind === 'xml' ? 'pasted.xml' : 'pasted.txt';
  takeFiles([new File([bytes], name, { type: kind === 'xml' ? 'application/xml' : 'text/plain' })]);
}

/* ── samples ───────────────────────────────────────────────────────── */
async function sample(id) {
  const sm = CFG.samples && CFG.samples[id];
  if (!sm) return;
  try {
    const r = await fetch(sm.url, { credentials: 'same-origin' });
    const b = await r.blob();
    takeFiles([new File([b], sm.name, { type: b.type })], { sample: true });
  } catch (e) { window.__handleError && window.__handleError(e, 'e_invoice_sample', { say: ui('read_failed') }); }
}

/* ── boot ──────────────────────────────────────────────────────────── */
function boot() {
  const zone = $('eiDrop');
  const onFiles = (detail) => {
    const files = (detail && detail.files && detail.files.length) ? Array.from(detail.files) : detail && detail.file ? [detail.file] : [];
    takeFiles(files);
  };
  if (zone) {
    // второй аргумент помощника — сам detail (не событие): в нём files всей пачки
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, (f, detail) => onFiles(detail || { file: f }));
    else zone.addEventListener('tool-uploader:file', (e) => onFiles(e.detail));
    zone.addEventListener('tool-uploader:clear', () => { S.gen++; S.docs = []; S.cur = -1; renderAll(); $('ei-empty').hidden = false; });
  }
  $('ei-paste-go').addEventListener('click', pasted);
  const qrIn = $('ei-qr-input');
  if (qrIn) qrIn.addEventListener('change', () => { if (qrIn.files && qrIn.files.length) takeFiles(Array.from(qrIn.files)); qrIn.value = ''; });
  document.querySelectorAll('[data-sample]').forEach((b) => b.addEventListener('click', () => sample(b.dataset.sample)));
  document.querySelectorAll('#ei-tabs [data-tab]').forEach((b) => b.addEventListener('click', () => setTab(b.dataset.tab)));
  document.querySelectorAll('#ei-danfe-layout [data-layout]').forEach((b) => b.addEventListener('click', () => { danfeLayout = b.dataset.layout; const d = S.docs[S.cur]; if (d && d.nfe) renderDanfePreview(d); }));
  $('ei-act-pdf').addEventListener('click', downloadPdf);
  $('ei-act-xml').addEventListener('click', downloadXml);
  $('ei-back').addEventListener('click', () => {
    const row = S.cur >= 0 && S.docs[S.cur] ? document.querySelector('#ei-list tr[data-uid="' + S.docs[S.cur].uid + '"]') : null;
    S.cur = -1;
    $('ei-detail').hidden = true;
    renderAll();
    // Back to the row that was open, not to the top of a long list.
    const back = row && document.querySelector('#ei-list tr[data-uid="' + row.dataset.uid + '"] .ei-open');
    if (back) { back.scrollIntoView({ block: 'nearest' }); back.focus({ preventScroll: true }); }
  });
  const rr = $('ei-run-rules');
  if (rr) rr.addEventListener('click', runRulesNow);
  const note = $('ei-act-note');
  if (note) note.addEventListener('click', copyNote);
  const edit = $('ei-act-edit');
  if (edit) edit.addEventListener('click', editInForm);
  const filt = $('ei-filter');
  if (filt) filt.addEventListener('click', (e) => {
    const b = e.target.closest('[data-filter]');
    if (!b) return;
    S.filter = b.dataset.filter;
    renderAll();
    ae('feature_use', { f: 'einvoice_filter', v: S.filter });
  });
  // ↑ / ↓ walk the list (the open file follows the focus beside it on a wide screen).
  $('ei-list').addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
    const btns = [...document.querySelectorAll('#ei-list .ei-open')];
    const k = btns.indexOf(document.activeElement);
    if (k < 0) return;
    const next = btns[k + (e.key === 'ArrowDown' ? 1 : -1)];
    if (!next) return;
    e.preventDefault();
    next.focus();
    if (splitShown()) open(+next.dataset.i);
  });
  $('ei-batch-pdf').addEventListener('click', () => batchDownload('zip'));
  $('ei-batch-csv').addEventListener('click', () => batchDownload('csv'));
  $('ei-batch-lines').addEventListener('click', () => batchDownload('lines'));
  // Mode switch: open/check ↔ issue.
  document.querySelectorAll('#ei-mode [data-mode]').forEach((b) => b.addEventListener('click', () => setMode(b.dataset.mode)));
  window.addEventListener('resize', () => { const host = $('ei-view-danfe-sheets'); if (host && !host.closest('[hidden]')) fitSheets(host); });
  // An invoice handed over by /invoice-generator («Make an e-invoice»): read once, 30 minutes fresh.
  let handed = null;
  try { handed = JSON.parse(sessionStorage.getItem('ei:handoff') || 'null'); sessionStorage.removeItem('ei:handoff'); } catch (_) {}
  if (handed && handed.state && handed.at && Date.now() - handed.at < 30 * 60 * 1000) {
    setMode('create');
    if (createApi) createApi.loadState(handed.state, { label: handed.state.number || '' });
    ae('feature_use', { f: 'einvoice_handoff_in', from: String(handed.from || '') });
  } else setMode(/#create/.test(location.hash) ? 'create' : 'open');
  window.__EI = { takeFiles, state: S };
}

let createReady = false;
function setMode(mode) {
  document.querySelectorAll('#ei-mode [data-mode]').forEach((b) => { const on = b.dataset.mode === mode; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
  $('ei-open-pane').hidden = mode !== 'open';
  $('ei-create-pane').hidden = mode !== 'create';
  // Each mode has its own daily cap. The limit module mirrors a block it cannot
  // see next to the upload zone — unless the TOOL withdrew it (hidden on the
  // block's own root): so the other mode's block is withdrawn, not just unseen.
  document.querySelectorAll('[data-tool-limit]').forEach((el) => {
    const mine = mode === 'create' ? el.dataset.toolLimit === 'e-invoice-create' : el.dataset.toolLimit === 'e-invoice';
    el.hidden = !mine;
  });
  try { window.dispatchEvent(new Event('resize')); } catch (_) {}
  if (mode === 'create' && !createReady) { createReady = true; createApi = initCreate({ T, CFG, lib, validate: runScenario, scenarioFor }); }
  stage(mode === 'create' ? 'create_opened' : 'open_opened');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
